import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState, useRef } from 'react';
import useEventTicketQueries from '@dashboard/hooks/queries/useEventTicketQueries';
import Button from '../../../Button';
import { pageTicketTypesKey } from '@/dashboard/lib/queryKeys';
import Dialog from '../../../Dialog';
import TicketTypeMultiSelectList from './TicketTypeMultiSelectList';
import PropTypes from 'prop-types';

const propTypes = {
  page: PropTypes.object,
  onChange: PropTypes.func,
  initialValue: PropTypes.array,
};

const TicketTypeModalCheckbox = ({ page, onChange, initialValue = [] }) => {
  const { getTicketTypesForPage } = useEventTicketQueries();
  const [open, setOpen] = useState(false);
  const [selectedTicketTypes, setSelectedTicketTypes] = useState(new Map());
  // Store the original selected ticket types in a separate map. We use this to "reset" the selection
  const [originalSelectedTicketTypes, setOriginalSelectedTicketTypes] = useState(new Map());
  const hasInitialized = useRef(initialValue.length > 0 ? false : true);
  const currency = page.eventDetails?.currency;
  const pageId = page._id;

  const { data: ticketTypesData } = useQuery({
    queryKey: pageTicketTypesKey(pageId),
    queryFn: () => getTicketTypesForPage(pageId),
    enabled: !!pageId,
  });

  useEffect(() => {
    if (hasInitialized.current === false && ticketTypesData?.length > 0) {
      const newMap = new Map();

      initialValue.forEach(ticketTypeId => {
        const ticket = ticketTypesData.find(t => t._id === ticketTypeId);
        if (ticket) {
          newMap.set(ticketTypeId, ticket);
        }
      });

      setSelectedTicketTypes(newMap);
      setOriginalSelectedTicketTypes(newMap);
      hasInitialized.current = true;
    }
  }, [ticketTypesData, initialValue]);

  const toggleTicketType = ticketType => {
    setSelectedTicketTypes(prev => {
      const newMap = new Map(prev);

      if (newMap.has(ticketType._id)) {
        newMap.delete(ticketType._id);
      } else {
        newMap.set(ticketType._id, ticketType);
      }

      return newMap;
    });
  };

  const handleOpenModal = () => {
    setOriginalSelectedTicketTypes(new Map(selectedTicketTypes));
    setOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTicketTypes(new Map(originalSelectedTicketTypes));
    setOpen(false);
  };

  const handleSave = () => {
    const selectedIds = Array.from(selectedTicketTypes.keys());
    onChange(selectedIds);
    setOriginalSelectedTicketTypes(new Map(selectedTicketTypes));
    setOpen(false);
  };

  return (
    <div className="flex flex-col mt-2">
      <Dialog
        trigger={
          <div className="ml-auto">
            <Button appearance="secondary" size="small" onClick={handleOpenModal}>
              Choose tickets
            </Button>
          </div>
        }
        title="Limit to tickets"
        description="The discount is applied to each ticket the customer selects at checkout."
        open={open}
        onCancel={handleCloseModal}
        size="md"
        cancelButton={null}
        confirmButton={{
          label: 'Save',
          appearance: 'primary',
          size: 'full',
          onClick: handleSave,
        }}
      >
        <div className="flex flex-col gap-y-1">
          <TicketTypeMultiSelectList
            ticketTypes={ticketTypesData || []}
            selectedTicketTypes={selectedTicketTypes}
            onToggle={toggleTicketType}
            interactive={true}
            currency={currency}
          />
        </div>
      </Dialog>
    </div>
  );
};

TicketTypeModalCheckbox.propTypes = propTypes;

export default TicketTypeModalCheckbox;
